import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ROLE_LABELS, isManager } from "@/lib/constants";
import {
  LayoutDashboard, Building2, Users, BellRing, Settings, Mail,
  LogOut, Menu, ShieldCheck, Boxes, FileStack, CalendarCheck, Flame, Truck, CalendarPlus, DatabaseBackup,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

function NavItems({ role, onNavigate, showDrivers }) {
  const items = [
    { to: "/", label: "Ana Ekran", icon: LayoutDashboard, roles: ["main_admin", "org_manager", "advisor", "customer"], end: true, testId: "nav-dashboard" },
    { to: "/firmalar", label: "Firmalar", icon: Building2, roles: ["main_admin", "org_manager", "advisor"], testId: "nav-firms" },
    { to: "/yil-yonetimi", label: "Yıl Yönetimi", icon: CalendarPlus, roles: ["main_admin", "org_manager", "advisor"], testId: "nav-year-management" },
    { to: "/katalog", label: "Evrak Kataloğu", icon: FileStack, roles: ["main_admin"], testId: "nav-catalog" },
    { to: "/kullanicilar", label: "Kullanıcılar", icon: Users, roles: ["main_admin", "org_manager"], testId: "nav-users" },
    { to: "/personel-bildirimleri", label: "Personel Bildirimleri", icon: BellRing, roles: ["main_admin", "org_manager", "advisor", "customer"], testId: "nav-notifications" },
    { to: "/belgelerim", label: "Belge Klasörlerim", icon: Boxes, roles: ["customer"], testId: "nav-customer-folders" },
    { to: "/ziyaret-raporlarim", label: "Ziyaret Raporları", icon: CalendarCheck, roles: ["customer"], testId: "nav-customer-visits" },
    { to: "/egitim-tatbikatlar", label: "Eğitim ve Tatbikatlar", icon: Flame, roles: ["customer"], testId: "nav-customer-training" },
    { to: "/surucu-arac", label: "Sürücü ve Araç Belgeleri", icon: Truck, roles: ["customer"], testId: "nav-customer-drivers", requireDrivers: true },
    { to: "/kurulus-ayarlari", label: "Kuruluş Ayarları", icon: Settings, roles: ["main_admin", "org_manager"], testId: "nav-org-settings" },
    { to: "/giden-epostalar", label: "Giden E-postalar", icon: Mail, roles: ["main_admin", "org_manager"], testId: "nav-emails" },
    { to: "/yedekleme", label: "Yedekleme", icon: DatabaseBackup, roles: ["main_admin"], testId: "nav-backup" },
  ];
  return (
    <nav className="flex flex-col gap-1">
      {items.filter((i) => i.roles.includes(role) && (!i.requireDrivers || showDrivers)).map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          end={i.end}
          onClick={onNavigate}
          data-testid={i.testId}
          className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
        >
          <i.icon className="h-4 w-4 shrink-0" />
          <span>{i.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function Brand({ org }) {
  return (
    <div className="flex items-center gap-2.5" data-testid="org-brand">
      {org?.logo ? (
        <img src={org.logo} alt="logo" className="h-9 w-9 rounded-md object-cover bg-white" />
      ) : (
        <div className="h-9 w-9 rounded-md bg-sky-600 flex items-center justify-center">
          <ShieldCheck className="h-5 w-5 text-white" />
        </div>
      )}
      <div className="leading-tight">
        <div className="font-display font-bold text-white text-sm" data-testid="org-short-name">
          {org?.short_name || "TMGD Portalı"}
        </div>
        <div className="text-[11px] text-slate-400">Tehlikeli Madde Güvenlik Danışmanlığı</div>
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, org, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showDrivers, setShowDrivers] = useState(false);

  useEffect(() => {
    if (user.role === "customer" && user.firm_id) {
      api.get(`/firms/${user.firm_id}`).then(({ data }) => setShowDrivers((data.activities || []).includes("tasiyan"))).catch(() => {});
    }
  }, [user.role, user.firm_id]);

  const Sidebar = (
    <div className="flex h-full flex-col bg-slate-900">
      <div className="px-4 py-5 border-b border-slate-800">
        <Brand org={org} />
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-4">
        <NavItems role={user.role} showDrivers={showDrivers} onNavigate={() => setOpen(false)} />
      </div>
      <div className="px-3 py-4 border-t border-slate-800">
        <div className="px-3 pb-3">
          <div className="text-sm font-medium text-white">{user.name}</div>
          <div className="text-xs text-slate-400">{ROLE_LABELS[user.role]}</div>
        </div>
        <button
          onClick={() => { logout(); navigate("/giris"); }}
          data-testid="logout-button"
          className="sidebar-link w-full text-left"
        >
          <LogOut className="h-4 w-4" /> Çıkış Yap
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden lg:block w-64 shrink-0">
        <div className="fixed w-64 h-screen">{Sidebar}</div>
      </aside>
      <div className="flex-1 min-w-0 lg:ml-0">
        <header className="sticky top-0 z-30 h-16 bg-white/95 backdrop-blur border-b border-slate-200 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64 border-0">
                {Sidebar}
              </SheetContent>
            </Sheet>
            <div className="lg:hidden"><Brand org={{ ...org, logo: org?.logo }} /></div>
            <div className="hidden lg:block">
              <div className="font-display font-semibold text-slate-900">{org?.full_title || org?.short_name || "TMGD Portalı"}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center rounded-full bg-sky-50 border border-sky-200 px-3 py-1 text-xs font-semibold text-sky-700" data-testid="role-badge">
              {ROLE_LABELS[user.role]}
            </span>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">{children}</main>
      </div>
    </div>
  );
}
